import { useCallback, useEffect, useMemo, useState } from 'react'
import { Loader2, LocateFixed, MapPin, Pause, Play, RotateCcw } from 'lucide-react'
import type { Alert, Severity, Vehicle, VehicleMeta } from '../lib/types'
import { replayVehiclePosition, type TripPathPoint } from '../lib/tripPath'
import { ago, clock, dateTime } from '../lib/format'
import { TONE_HEX } from '../cc/tokens'
import { collapseSameLegAlerts } from '../lib/recurring'
import { CATEGORY_LABEL, cycleStatusLabel, fmtDuration, type PlaybackCycle } from './RoundTripCycles'

/** Same triple (danger/warn/info) every other severity chip in this console uses --
 *  see CommandCentre.tsx's own per-file `SEV_TONE` -- not new colours. */
const ALERT_SEV_COLOR: Record<Severity, string> = {
  CRITICAL: TONE_HEX.danger,
  WARN: TONE_HEX.warn,
  INFO: TONE_HEX.info,
}

/** Operator-facing multipliers. Default 8× — followable, not a corridor blink. */
export const TRIP_REPLAY_SPEEDS = [1, 4, 8, 16] as const
export type TripReplaySpeed = (typeof TRIP_REPLAY_SPEEDS)[number]
export const TRIP_REPLAY_DEFAULT_SPEED: TripReplaySpeed = 8
/** Wall-clock interval for the playhead timer. */
export const TRIP_REPLAY_TICK_MS = 200

/** Event-time advance per wall-clock tick. 8× ⇒ 1.6s of trip per 200ms. */
export function tripReplayAdvanceMs(speed: number, tickMs = TRIP_REPLAY_TICK_MS): number {
  return Math.max(1, speed) * tickMs
}

function fmtStationary(sec: number): string {
  const h = Math.floor(sec / 3600)
  const m = Math.round((sec % 3600) / 60)
  return h > 0 ? `${h}h ${m}m` : `${m}m`
}

export function stepTripReplayPlayhead(
  at: number,
  end: number,
  speed: number,
  tickMs = TRIP_REPLAY_TICK_MS,
): { at: number; done: boolean } {
  const next = at + tripReplayAdvanceMs(speed, tickMs)
  if (next >= end) return { at: end, done: true }
  return { at: next, done: false }
}

/**
 * Replay a completed leg (or round trip) from Postgres geotags, halts, and trip_path GPS.
 *
 * Positions follow interpolated GPS on trip_path when fixes exist; geotag/halt coords are
 * the fallback. The teal polyline shows the driven path up to the scrubber time.
 */

export interface TripReplayEvent {
  kind: 'geotag' | 'halt' | string
  ts: number
  end_ts?: number
  event?: string
  geofence_id?: string
  geofence_name?: string
  fence_type?: string
  hub_code?: string | null
  site?: string | null
  lat?: number | null
  lon?: number | null
  duration_sec?: number
  dwell_sec?: number | null
  scheduled?: boolean
  load_state?: string | null
}

export interface TripReplayPayload {
  kind: 'trip' | 'round_trip' | 'breakdown'
  trip_id?: string | null
  round_trip_id?: string | null
  breakdown_id?: number | null
  vehicle_id: string
  started_ts: number
  ended_ts: number | null
  origin_hub?: string | null
  destination_hub?: string | null
  completed?: boolean
  diverted?: boolean | null
  still_open?: boolean
  /** Round-trip playback (/api/round-trips/playback): a trip not yet back in Dhar plays
   *  up to this moment -- when it was loaded -- instead of stopping at its last geotag. */
  live_until?: number | null
  /** Round-trip playback only: category / status / phase / milestones of the cycle. */
  playback?: PlaybackCycle
  /** breakdown replay only: when it was reported, if that's earlier than started_ts --
   *  the scrubber starts at resumed-moving, not report time, since there is nothing to
   *  play through a stop that never moved (see stationary_sec). */
  reported_ts?: number | null
  /** Seconds stationary between being reported and resuming -- 0 for a normal trip. */
  stationary_sec?: number
  events: TripReplayEvent[]
  geotag_count: number
  halt_count: number
  /** breakdown replay only: real charging windows overlapping the incident -- EVOPT's
   *  own live-session record when recent enough, else hub_visit's SOC/geofence-derived
   *  charge_started_ts/charge_ended_ts for anything past EVOPT's 1-day retention. */
  charging_spans?: {
    start_ts: number
    end_ts: number
    start_soc_pct?: number | null
    end_soc_pct?: number | null
  }[]
}

export interface RecentTrip {
  trip_id: string
  vehicle_id: string
  round_trip_id?: string | null
  origin_hub?: string | null
  destination_hub?: string | null
  started_ts: number
  ended_ts: number | null
  duration_sec?: number | null
  load_state?: string | null
  driver_name?: string
  geotags?: number
  halts?: number
  completed?: boolean
  // The parent round trip's own identity, present only when round_trip_id is set --
  // what lets a leg say which trip it belongs to instead of a bare, contextless flag.
  rt_origin_hub?: string | null
  rt_far_hub?: string | null
  rt_started_ts?: number | null
  rt_leg_count?: number | null
  rt_completed?: boolean | null
}

export type ReplayLegKind = 'completed' | 'active'

export interface ReplayLegPick {
  leg: RecentTrip
  kind: ReplayLegKind
}

/** Pick the best replayable leg: latest completed, else in-progress with geotags/halts. */
export function pickReplayLeg(trips: readonly RecentTrip[]): ReplayLegPick | null {
  if (!trips.length) return null
  const completed = trips.find((t) => t.completed !== false)
  if (completed) return { leg: completed, kind: 'completed' }
  const latest = trips[0]
  if (latest && ((latest.geotags ?? 0) > 0 || (latest.halts ?? 0) > 0)) {
    return { leg: latest, kind: 'active' }
  }
  return null
}

export interface TripReplayFrameResult {
  vehicles: Vehicle[]
  meta: Map<string, VehicleMeta>
}

function loadStateAt(events: TripReplayEvent[], at: number): string | null {
  let state: string | null = null
  for (const e of events) {
    if ((e.ts || 0) > at) break
    if (e.load_state) state = e.load_state
  }
  return state
}

function replayVehicleStub(
  vehicleId: string,
  lat: number,
  lon: number,
  at: number,
  moving: boolean,
  speed_kph: number | null,
  heading_deg: number | null,
): Vehicle {
  return {
    vehicle_id: vehicleId,
    device_id: null,
    updated_ts: at,
    last_obd_ts: at,
    last_geo_ts: at,
    lat,
    lon,
    speed_kph,
    heading_deg,
    satellites: null,
    accuracy_level: null,
    soc_pct: null,
    coolant_c: null,
    mcu_temp_c: null,
    motor_rpm: null,
    odo_raw: null,
    odo_km: null,
    pack_voltage: null,
    pack_current: null,
    power_kw: null,
    energy_kwh_session: null,
    discharge_kwh_session: null,
    regen_kwh_session: null,
    idle_kwh_session: null,
    charge_kwh_session: null,
    odo_km_session: null,
    gps_km_session: null,
    live_kwh_per_km: null,
    battery_state: null,
    charger_state: 0,
    moving,
    obd_frames: 0,
    geo_fixes: 0,
    sentinel_count: 0,
    obd_lag_sec: null,
    geo_lag_sec: null,
  }
}

export function useTripReplayFrame(
  payload: TripReplayPayload | null,
  at: number,
  path?: TripPathPoint[] | null,
): TripReplayFrameResult | null {
  return useMemo(() => {
    if (!payload?.events.length) return null
    const pos = replayVehiclePosition(payload.events, path, at)
    if (!pos) return null

    const v = replayVehicleStub(
      payload.vehicle_id,
      pos.lat,
      pos.lon,
      at,
      !pos.halted,
      pos.speed_kph,
      pos.heading_deg,
    )
    const load = loadStateAt(payload.events, at)
    const meta = new Map<string, VehicleMeta>()
    if (load) {
      meta.set(payload.vehicle_id, {
        vehicle_id: payload.vehicle_id,
        device_id: null,
        fleet: null,
        depot: null,
        route_id: null,
        route_name: null,
        load_state: load,
        model: null,
        capacity_kwh: null,
      })
    }
    return { vehicles: [v], meta }
  }, [payload, at, path])
}

/**
 * The flat list from /api/trips/recent mixes two different things -- an individual
 * hub-to-hub leg, and (when round_trip_id is set) one leg of a larger round trip --
 * with nothing distinguishing them beyond a bare "round" tag on the leg. That reads as
 * "what is this?" rather than answering it.
 *
 * This groups every leg that shares a round_trip_id under one header naming that trip
 * (route, leg count, when it started) so a leg's answer to "which trip is this part of"
 * is which header it sits under, not a flag with no information in it. A round trip's
 * legs are not always contiguous in the flat list (interleaved with another vehicle's),
 * so grouping is by round_trip_id, not by adjacency -- each trip still renders once, at
 * the position of its most recent leg. Standalone legs (no round_trip_id) render plainly,
 * in their own list position, with no tag at all -- ungrouped already reads as standalone.
 */
type PickerItem =
  | { type: 'round_trip'; roundTripId: string; legs: RecentTrip[] }
  | { type: 'leg'; trip: RecentTrip }

function groupByRoundTrip(trips: readonly RecentTrip[]): PickerItem[] {
  const items: PickerItem[] = []
  const groupIndex = new Map<string, number>()
  for (const t of trips) {
    if (!t.round_trip_id) {
      items.push({ type: 'leg', trip: t })
      continue
    }
    const idx = groupIndex.get(t.round_trip_id)
    if (idx == null) {
      groupIndex.set(t.round_trip_id, items.length)
      items.push({ type: 'round_trip', roundTripId: t.round_trip_id, legs: [t] })
    } else {
      const item = items[idx]
      if (item?.type === 'round_trip') item.legs.push(t)
    }
  }
  return items
}

function LegRow({ t, activeId, onPick, indent }: {
  t: RecentTrip
  activeId?: string | null
  onPick: (t: RecentTrip) => void
  /** Nested under a round-trip header -- lighter weight than a standalone row. */
  indent?: boolean
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={() => onPick(t)}
      className={`w-full rounded-[10px] border px-2.5 py-2 text-left transition-colors ${
        activeId === t.trip_id
          ? 'border-[rgba(37,99,235,0.35)] bg-[#EFF6FF]'
          : indent
            ? 'border-transparent bg-[#F8FAFC] hover:bg-[#F1F5F9]'
            : 'border-[rgba(15,23,42,0.07)] bg-white hover:bg-[#F8FAFC]'
      }`}
    >
      <div className="flex items-baseline gap-2">
        <span className="font-mono text-[12px] font-semibold text-[#0F172A]">
          {t.vehicle_id}
        </span>
        <span className="truncate text-[11px] text-[#64748B]">
          {t.origin_hub ?? '?'} → {t.destination_hub ?? '?'}
        </span>
      </div>
      <div className="mt-0.5 flex flex-wrap gap-x-2 text-[10px] text-[#94A3B8]">
        <span>{t.driver_name}</span>
        <span>{dateTime(t.started_ts)}</span>
        <span>{t.geotags ?? 0} geotags · {t.halts ?? 0} halts</span>
      </div>
    </button>
  )
}

export function TripReplayPicker({
  onPick, activeId,
}: { onPick: (t: RecentTrip) => void; activeId?: string | null }): JSX.Element {
  const [trips, setTrips] = useState<RecentTrip[]>([])
  const [err, setErr] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)

  const load = useCallback(() => {
    setLoading(true)
    fetch('/api/trips/recent?limit=40')
      .then(async (r) => {
        const body = await r.json() as { trips?: RecentTrip[]; error?: string }
        if (!r.ok || body.error) throw new Error(body.error ?? r.statusText)
        setTrips(body.trips ?? [])
        setErr(null)
      })
      .catch((e: unknown) => setErr(e instanceof Error ? e.message : String(e)))
      .finally(() => setLoading(false))
  }, [])

  useEffect(() => { load() }, [load])

  if (loading) {
    return (
      <div className="flex items-center gap-2 px-2 py-4 text-[12px] text-[#64748B]">
        <Loader2 size={14} className="animate-spin" /> Loading trips…
      </div>
    )
  }
  if (err) {
    return <p className="px-2 py-3 text-[12px] text-[#B91C1C]">{err}</p>
  }
  if (!trips.length) {
    return (
      <p className="px-2 py-3 text-[12px] text-[#64748B]">
        No completed legs recorded yet for this vehicle.
      </p>
    )
  }

  const items = groupByRoundTrip(trips)

  return (
    <ul className="min-h-0 flex-1 space-y-1.5 overflow-auto pr-0.5">
      {items.map((item) => item.type === 'leg' ? (
        <li key={item.trip.trip_id}>
          <LegRow t={item.trip} activeId={activeId} onPick={onPick} />
        </li>
      ) : (
        <li key={item.roundTripId} className="rounded-[10px] border border-[rgba(37,99,235,0.16)] bg-[#F8FAFC] p-1.5">
          <div className="flex items-baseline gap-1.5 px-1 pb-1.5 pt-0.5">
            <span className="text-[10px] font-semibold uppercase tracking-wide text-[#2563EB]">
              Round trip
            </span>
            <span className="truncate text-[11px] font-medium text-[#334155]">
              {item.legs[0]?.rt_origin_hub ?? '?'} ⇄ {item.legs[0]?.rt_far_hub ?? '?'}
            </span>
            <span className="text-[10px] text-[#94A3B8]">
              {item.legs[0]?.rt_leg_count ?? item.legs.length} legs
              {' · '}started {dateTime(item.legs[0]?.rt_started_ts)}
              {item.legs[0]?.rt_completed === false ? ' · in progress' : ''}
            </span>
          </div>
          <div className="space-y-1">
            {item.legs.map((t) => (
              <LegRow key={t.trip_id} t={t} activeId={activeId} onPick={onPick} indent />
            ))}
          </div>
        </li>
      ))}
    </ul>
  )
}

/** The replay's own [start, end] window -- extracted from what used to be inline in
 *  TripReplayBar (started_ts..ended_ts, falling back to the last event's ts when the
 *  replay is still open) so the axis and the alert correlation below cannot drift apart
 *  by one being edited without the other. */
export function tripReplayWindow(payload: TripReplayPayload): { start: number; end: number } {
  const start = payload.started_ts
  const end = payload.ended_ts ?? payload.live_until ?? (payload.events.at(-1)?.ts || start + 1)
  return { start, end }
}

/** The actual left edge alerts are correlated against -- `tripReplayWindow`'s own
 *  `start` widened to `reported_ts` for a breakdown reported before it resumed moving
 *  (see `tripReplayAlerts`'s own doc for why). Exported on its own (round-3 adversarial
 *  review, MED-1) so CommandCentre.tsx's coverage-uncertain check reads the SAME value
 *  `tripReplayAlerts` actually correlates against, instead of recomputing a narrower one
 *  from `tripReplayWindow` alone and drifting out of sync with it. */
export function tripReplayCorrelationStart(payload: TripReplayPayload): number {
  const { start } = tripReplayWindow(payload)
  return payload.reported_ts != null ? Math.min(start, payload.reported_ts) : start
}

/** Alerts worth showing on this one replay: this payload's own vehicle, inside its own
 *  window -- not "whatever the live store currently holds," which is unrelated to what
 *  was happening during the trip being replayed.
 *
 *  Two adjustments on top of a flat window filter:
 *  - A breakdown replay's own axis starts at resumed-moving (`started_ts`), not report
 *    time -- `reported_ts` can be well before that (see TripReplayPayload's own doc).
 *    The alert that triggered the breakdown report usually fires AT OR NEAR report time,
 *    which would otherwise be silently excluded from the one replay opened specifically
 *    to find it. Correlation widens to include it (`tripReplayCorrelationStart`); the
 *    axis itself is untouched (see TripReplayBar's clamp on the dot's own position for
 *    the on-screen consequence).
 *  - Collapsed through `collapseSameLegAlerts`, the same helper the Alerts tab already
 *    runs every list through -- without it, a same-leg OVERSPEED burst that reads as one
 *    card there would paint a cluster of coincident dots here instead. */
export function tripReplayAlerts(payload: TripReplayPayload, alerts: readonly Alert[]): Alert[] {
  const { end } = tripReplayWindow(payload)
  const correlationStart = tripReplayCorrelationStart(payload)
  return collapseSameLegAlerts(
    alerts.filter((a) => a.vehicle_id === payload.vehicle_id && a.ts >= correlationStart && a.ts <= end),
  )
}

export function TripReplayBar({
  payload, at, playing, speed, follow, onSeek, onToggle, onClear, onSpeed, onFollow, onPlayRound,
  alerts, alertsCoverageUncertain,
}: {
  payload: TripReplayPayload
  at: number
  playing: boolean
  speed: number
  follow: boolean
  onSeek: (ts: number) => void
  onToggle: () => void
  onClear: () => void
  onSpeed: (s: TripReplaySpeed) => void
  onFollow: () => void
  onPlayRound?: () => void
  /** Full store alert list -- filtered internally (tripReplayAlerts) to this payload's
   *  own vehicle + time window. Omit entirely for today's behaviour (no alert row). */
  alerts?: readonly Alert[]
  /** True when `alerts` is known not to reach back as far as this replay's own window --
   *  CommandCentre.tsx's own tripReplayAlertsCoverageUncertain. "0 alerts" must not read
   *  as "clean trip" when the real reason is "the data we have doesn't cover this far
   *  back" -- those are different facts and this says which one is true. */
  alertsCoverageUncertain?: boolean
}): JSX.Element {
  const { start, end } = tripReplayWindow(payload)
  const span = Math.max(1, end - start)
  const tripAlerts = useMemo(() => tripReplayAlerts(payload, alerts ?? []), [payload, alerts])

  // title is the whole fix for "time of events missing on the timeline" -- every tick
  // used to render with no tooltip at all, so the only way to learn when something
  // happened was to drag the scrubber onto it pixel-by-pixel and read the status line.
  const ticks = useMemo(() => payload.events.map((e) => ({
    x: (((e.ts || start) - start) / span) * 100,
    halt: e.kind === 'halt',
    boundary: Boolean(e.event && e.hub_code),
    title: `${clock(e.ts || start)}${e.event ? ` · ${e.event}` : ''}`
      + `${e.geofence_name || e.hub_code ? ` · ${e.geofence_name || e.hub_code}` : ''}`,
  })), [payload.events, start, span])

  // Halted SPANS, not just point ticks -- an EXIT event's own dwell_sec is how long the
  // vehicle sat still before it (kind: 'geotag' breakdown/trip events never carry
  // kind === 'halt', so ticks[].halt above never fires for them; this is the real signal).
  const haltSpans = useMemo(() => {
    const spans: { x: number; w: number; sec: number; label: string; startTs: number; endTs: number }[] = []
    for (const e of payload.events) {
      if (e.event !== 'EXIT' || !e.dwell_sec || e.dwell_sec <= 0) continue
      const exitTs = e.ts
      const enterTs = exitTs - e.dwell_sec * 1000
      const x = ((Math.max(enterTs, start) - start) / span) * 100
      const xEnd = ((Math.min(exitTs, end) - start) / span) * 100
      if (xEnd <= x) continue
      spans.push({
        x, w: xEnd - x, sec: e.dwell_sec, label: e.geofence_name || e.hub_code || '',
        startTs: Math.max(enterTs, start), endTs: Math.min(exitTs, end),
      })
    }
    return spans
  }, [payload.events, start, end, span])

  // Real charging windows overlapping the replay, own row so they never visually merge
  // with an amber halt span at the same hub -- a truck can be both stationary AND
  // charging, and the two facts read differently to an operator (stationary is downtime,
  // charging is downtime being used well).
  const chargingSpans = useMemo(() => {
    const spans: { x: number; w: number; startTs: number; endTs: number; socIn: number | null | undefined; socOut: number | null | undefined }[] = []
    for (const c of payload.charging_spans ?? []) {
      const x = ((Math.max(c.start_ts, start) - start) / span) * 100
      const xEnd = ((Math.min(c.end_ts, end) - start) / span) * 100
      if (xEnd <= x) continue
      spans.push({
        x, w: xEnd - x, startTs: Math.max(c.start_ts, start), endTs: Math.min(c.end_ts, end),
        socIn: c.start_soc_pct, socOut: c.end_soc_pct,
      })
    }
    return spans
  }, [payload.charging_spans, start, end, span])

  const current = useMemo(() => {
    const past = payload.events.filter((e) => (e.ts || 0) <= at)
    return past[past.length - 1] ?? null
  }, [payload.events, at])

  const haltedNow = useMemo(
    () => haltSpans.find((h) => {
      const x = ((at - start) / span) * 100
      return x >= h.x && x <= h.x + h.w
    }) ?? null,
    [haltSpans, at, start, span],
  )

  const chargingNow = useMemo(
    () => chargingSpans.find((c) => {
      const x = ((at - start) / span) * 100
      return x >= c.x && x <= c.x + c.w
    }) ?? null,
    [chargingSpans, at, start, span],
  )

  // Below 5 minutes reads as normal reporting lag, not worth calling out.
  const stationaryNotice = (payload.stationary_sec ?? 0) >= 300
    ? fmtStationary(payload.stationary_sec!)
    : null

  return (
    <div className="shrink-0 border-t border-[rgba(37,99,235,0.28)] bg-[#EFF6FF] px-4 py-2">
      <div className="mb-1.5 flex flex-wrap items-center gap-2">
        <span className="text-[10px] font-semibold uppercase tracking-wider text-[#1D4ED8]">
          {payload.kind === 'breakdown' ? 'Breakdown replay' : payload.playback ? 'Round trip replay' : 'Trip replay'}
        </span>
        <span className="font-mono text-[12px] text-[#0F172A]">{payload.vehicle_id}</span>
        {payload.playback ? (
          <>
            <span className="text-[11px] text-[#475569]">
              {payload.playback.category === 'short_return' ? 'Dhar → Dhar (did not reach Dhule)' : 'Dhar → Dhule → Dhar'}
            </span>
            <span className={`rounded-full px-1.5 py-px text-[10px] font-semibold ${
              payload.playback.category === 'short_return' ? 'bg-[#FFFBEB] text-[#92400E]' : 'bg-white text-[#1D4ED8]'
            }`}
            >
              {CATEGORY_LABEL[payload.playback.category]}
            </span>
            <span className={`flex items-center gap-1 text-[10.5px] ${payload.playback.status === 'live' ? 'font-semibold text-[#047857]' : 'text-[#64748B]'}`}>
              {payload.playback.status === 'live' && <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-[#10B981]" />}
              {cycleStatusLabel(payload.playback)}
              {payload.live_until ? ` · up to ${clock(payload.live_until).slice(0, 5)}` : ''}
            </span>
            <span className="text-[10.5px] text-[#64748B]" title="Start, Jhulwaniya, Dhule and return are found from the truck's GPS fixes inside the fence polygons">
              boundaries: GPS{payload.playback.gps_points != null ? ` · ${payload.playback.gps_points} fixes` : ''}
            </span>
            {payload.playback.gps_gap_warning ? (
              <span
                className="rounded-full border border-[rgba(217,119,6,0.35)] bg-[#FFFBEB] px-2 py-0.5 text-[10px] text-[#92400E]"
                title="The GPS feed was silent this long, and a Dhar/Dhule visit could fit inside it -- the trip's category may come from missing data"
              >
                GPS gap {fmtDuration(payload.playback.gps_max_gap_sec ?? 0)} — trip may be incomplete
              </span>
            ) : null}
          </>
        ) : (
          <span className="text-[11px] text-[#475569]">
            {payload.origin_hub ?? '?'} → {payload.destination_hub ?? (payload.still_open ? 'still travelling' : '?')}
          </span>
        )}
        {stationaryNotice && (
          <span
            className="rounded-full border border-[rgba(217,119,6,0.35)] bg-[#FFFBEB] px-2 py-0.5 text-[10px] text-[#92400E]"
            title="Time between being reported and resuming movement — shown as the amber halt span below"
          >
            stationary {stationaryNotice} before resuming
          </span>
        )}
        <span className="text-[11px] text-[#64748B]">
          {payload.geotag_count} geotags · {payload.halt_count} halts
          {payload.kind === 'trip' || payload.playback ? ' · GPS path' : ''}
          {/* Unconditional whenever `alerts` is actually wired (round-3 adversarial
              review, MED-3): omitting this for a zero-alert trip made "checked, found
              none" indistinguishable from "this isn't wired" -- an operator had no way
              to tell the difference. Omitted entirely only when the caller never passed
              `alerts` at all (today: no such caller exists, kept for signature
              backward-compat). */}
          {alerts !== undefined ? ` · ${tripAlerts.length} alerts` : ''}
        </span>
        {alertsCoverageUncertain && (
          <span
            className="rounded-full border border-[rgba(217,119,6,0.35)] bg-[#FFFBEB] px-2 py-0.5 text-[10px] text-[#92400E]"
            title="The loaded alert data doesn't reach back to when this trip started -- widen the Time filter to check for alerts before this point"
          >
            alert data may be incomplete
          </span>
        )}
        {payload.kind === 'trip' && onPlayRound && (
          <button
            type="button"
            onClick={onPlayRound}
            className="rounded-lg border border-[rgba(37,99,235,0.30)] px-2 py-0.5 text-[10px] text-[#1D4ED8] transition-colors duration-100 hover:bg-white"
          >
            Full round trip
          </button>
        )}
        <button
          type="button"
          onClick={onToggle}
          className="ml-auto flex items-center gap-1 rounded-lg border border-[rgba(15,23,42,0.12)] bg-white px-2 py-0.5 text-[11px] text-[#0F172A] transition-colors duration-100 hover:border-[rgba(37,99,235,0.45)] hover:text-[#1D4ED8]"
        >
          {playing ? <Pause size={12} /> : <Play size={12} />}
          {playing ? 'pause' : 'play'}
        </button>
        <span className="flex gap-0.5">
          {TRIP_REPLAY_SPEEDS.map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => onSpeed(s)}
              className={`rounded-lg border px-1.5 py-0.5 font-mono text-[9px] transition-colors duration-100 ${
                speed === s
                  ? 'border-[rgba(37,99,235,0.45)] bg-white text-[#1D4ED8]'
                  : 'border-[rgba(15,23,42,0.10)] text-[#64748B] hover:text-[#0F172A]'
              }`}
            >
              {s}×
            </button>
          ))}
        </span>
        <button
          type="button"
          onClick={onFollow}
          className={`flex items-center gap-1 rounded-lg border px-2 py-0.5 text-[11px] ${
            follow
              ? 'border-[rgba(37,99,235,0.45)] bg-white text-[#1D4ED8]'
              : 'border-[rgba(15,23,42,0.12)] text-[#64748B] hover:text-[#0F172A]'
          }`}
        >
          <LocateFixed size={12} />
          follow
        </button>
        <button
          type="button"
          onClick={onClear}
          className="flex items-center gap-1 rounded-lg border border-[rgba(15,23,42,0.12)] px-2 py-0.5 text-[11px] text-[#64748B] hover:text-[#0F172A]"
        >
          <RotateCcw size={12} /> live
        </button>
      </div>

      <div className="relative h-8">
        <div className="absolute inset-x-0 top-0 h-2">
          {haltSpans.map((h, i) => (
            <span
              key={i}
              className="absolute top-0 h-2 rounded-sm"
              style={{ left: `${h.x}%`, width: `${h.w}%`, background: 'rgba(217,119,6,0.35)' }}
              title={`${h.label ? `${h.label} — ` : ''}halted ${fmtStationary(h.sec)}`
                + ` · ${clock(h.startTs)} – ${clock(h.endTs)}`}
            />
          ))}
        </div>
        {chargingSpans.length > 0 && (
          <div className="absolute inset-x-0 top-[10px] h-2">
            {chargingSpans.map((c, i) => (
              <span
                key={i}
                className="absolute top-0 h-2 rounded-sm"
                style={{ left: `${c.x}%`, width: `${c.w}%`, background: 'rgba(5,150,105,0.4)' }}
                title={`charging${c.socIn != null && c.socOut != null ? ` · ${Math.round(c.socIn)}% → ${Math.round(c.socOut)}%` : ''}`
                  + ` · ${clock(c.startTs)} – ${clock(c.endTs)}`}
              />
            ))}
          </div>
        )}
        <div className="absolute inset-x-0 top-1 h-2">
          {ticks.map((t, i) => (
            <span
              key={i}
              className="absolute top-0 h-2 w-px"
              title={t.title}
              style={{
                left: `${t.x}%`,
                background: t.halt ? '#D97706' : t.boundary ? '#2563EB' : '#94A3B8',
                opacity: 0.85,
              }}
            />
          ))}
        </div>
        <input
          type="range"
          min={start}
          max={end}
          value={Math.min(end, Math.max(start, at))}
          onChange={(e) => onSeek(Number(e.target.value))}
          className="absolute inset-x-0 bottom-0 w-full accent-[#2563EB]"
        />
      </div>

      {/* Own strip, not squeezed into the scrubber above -- the native range input's hit
          area already covers that whole band, and a dot UNDER it would never receive a
          click. Same seek affordance as every other tick: onSeek, not a new callback. */}
      {tripAlerts.length > 0 && (
        <div className="relative mt-1 h-3">
          {tripAlerts.map((a) => (
            <button
              key={a.alert_id}
              type="button"
              onClick={() => onSeek(a.ts)}
              className="absolute top-0 h-2.5 w-2.5 -translate-x-1/2 rounded-full border border-white shadow-[0_0_0_1px_rgba(15,23,42,0.25)]"
              style={{
                // Clamped: a breakdown replay's correlation window can start earlier than
                // its own axis (reported_ts before started_ts, see tripReplayAlerts) --
                // pin that alert at the left edge rather than drawing it off-canvas.
                left: `${Math.min(100, Math.max(0, ((a.ts - start) / span) * 100))}%`,
                background: ALERT_SEV_COLOR[a.severity] ?? ALERT_SEV_COLOR.INFO,
              }}
              title={`${clock(a.ts)} · ${a.kind.replace(/_/g, ' ').toLowerCase()}`
                + `${a.message ? ` · ${a.message}` : ''}`
                // collapseSameLegAlerts (inside tripReplayAlerts) folds a same-leg burst
                // into one dot keyed on its LATEST firing -- without this, that merge is
                // invisible here even though the Alerts tab says "N events this leg" for
                // the exact same episode (DetailWorkspace.tsx/CommandCentre.tsx both read
                // _groupCount already; this is the one surface that hadn't).
                + (a._groupCount ? ` · ${a._groupCount}× from ${clock(a._groupFirstTs ?? a.ts)}` : '')}
            />
          ))}
        </div>
      )}

      <div className="mt-1 flex items-center justify-between text-[10px] text-[#64748B]">
        <span className="num">{clock(start)}</span>
        <span className="flex min-w-0 items-center gap-1 truncate text-[#0F172A]">
          <MapPin size={11} className={`shrink-0 ${haltedNow ? 'text-[#D97706]' : 'text-[#16A34A]'}`} />
          {haltedNow
            ? `Halted ${fmtStationary(haltedNow.sec)}${haltedNow.label ? ` @ ${haltedNow.label}` : ''}`
            : current?.kind === 'halt'
              ? `Halt ${current.duration_sec != null ? `${Math.round(current.duration_sec / 60)} min` : ''}`
              : (current?.geofence_name || current?.hub_code || 'moving')}
          {!haltedNow && current?.event ? ` · ${current.event}` : ''}
          {chargingNow && (
            <span className="text-[#059669]">
              · Charging{chargingNow.socIn != null && chargingNow.socOut != null
                ? ` ${Math.round(chargingNow.socIn)}%→${Math.round(chargingNow.socOut)}%` : ''}
            </span>
          )}
          <span className="text-[#94A3B8]">· {clock(at)} · {ago(at, Date.now())} ago</span>
        </span>
        <span className="num">{clock(end)}</span>
      </div>
    </div>
  )
}
